import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  Button,
  Divider,
  Avatar,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';

// Dummy news data
const newsData = {
  'union-negotiations': {
    title: 'Union Negotiations Progress',
    date: 'March 15, 2024',
    author: 'Richard Otieno',
    gradient: 'linear-gradient(45deg, #2e7d32 30%, #81c784 90%)',
    content: `
      <p>In a significant development for teachers across the district, our union negotiations have made substantial progress this week. The discussions, which have been ongoing for several months, have finally yielded positive results in key areas.</p>

      <h3>Key Achievements</h3>
      <ul>
        <li>Agreement on a 15% salary increase over the next three years</li>
        <li>Implementation of smaller class sizes, with a maximum of 25 students per class</li>
        <li>Enhanced professional development opportunities with dedicated funding</li>
        <li>Improved healthcare benefits package</li>
      </ul>

      <p>These developments represent a major step forward in our ongoing efforts to improve working conditions and compensation for teachers. The agreement, which will be put to a vote next week, has received preliminary support from both the union leadership and the school board.</p>

      <h3>Next Steps</h3>
      <p>Following the successful negotiations, we will be holding a series of town hall meetings to discuss the details of the agreement with our members. These meetings will provide an opportunity for teachers to ask questions and provide feedback before the final vote.</p>

      <p>We are particularly proud of the collaborative approach that led to these positive outcomes. By working together with the school board and maintaining open lines of communication, we were able to reach an agreement that benefits everyone involved.</p>
    `,
    tags: ['Union News', 'Negotiations', 'Teachers Rights']
  },
  'community-outreach': {
    title: 'Community Outreach Success',
    date: 'March 10, 2024',
    author: 'Richard Otieno',
    gradient: 'linear-gradient(45deg, #f9a825 30%, #ffd54f 90%)',
    content: `
      <p>Our campaign's community outreach program has reached a significant milestone, successfully engaging with over 500 local residents in the past month. This achievement marks a crucial step in building broad-based support for our education reform initiatives.</p>

      <h3>Program Highlights</h3>
      <ul>
        <li>Weekly community meetings in different neighborhoods</li>
        <li>Educational workshops for parents and community members</li>
        <li>Partnership with local businesses for resource sharing</li>
        <li>Student-led initiatives to promote education awareness</li>
      </ul>

      <p>The response from the community has been overwhelmingly positive, with many residents expressing their support for our vision of improved education standards and better support for teachers.</p>

      <h3>Community Impact</h3>
      <p>Through these outreach efforts, we've been able to:</p>
      <ul>
        <li>Build stronger relationships between schools and the community</li>
        <li>Gather valuable feedback on education priorities</li>
        <li>Create a network of community advocates for education reform</li>
        <li>Develop new partnerships for resource sharing and support</li>
      </ul>

      <p>We are committed to continuing these outreach efforts and expanding our community engagement programs in the coming months. The success of this initiative demonstrates the strong community support for our education reform agenda.</p>
    `,
    tags: ['Community', 'Outreach', 'Education Reform']
  }
};

function NewsPost() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const post = id ? newsData[id as keyof typeof newsData] : null;

  if (!post) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4">Post not found</Typography>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Back to Home
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate('/')}
        sx={{ mb: 3 }}
      >
        Back to Home
      </Button>

      <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: 2 }}>
        <Box
          sx={{
            width: '100%',
            height: 400,
            background: post.gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '3rem',
            fontWeight: 'bold',
            textAlign: 'center',
            p: 4,
          }}
        >
          {post.title}
        </Box>
        
        <Box sx={{ p: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
            {post.title}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
              <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {post.date}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {post.author}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            {post.tags.map((tag, index) => (
              <Chip 
                key={index}
                label={tag}
                sx={{ mr: 1, mb: 1 }}
                size="small"
              />
            ))}
          </Box>

          <Divider sx={{ mb: 4 }} />

          <Box 
            sx={{ 
              '& h3': { 
                mt: 4, 
                mb: 2,
                fontSize: '1.5rem',
                fontWeight: 'bold'
              },
              '& p': { 
                mb: 2,
                lineHeight: 1.7
              },
              '& ul': {
                mb: 2,
                pl: 3
              },
              '& li': {
                mb: 1,
                lineHeight: 1.7
              }
            }}
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </Box>
      </Paper>
    </Container>
  );
}

export default NewsPost; 